import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Plus, Trash2, GripVertical, Save, RefreshCw, Eye, Copy,
  Type, Hash, ToggleLeft, List, Calendar, Mail, Phone, Upload,
  MapPin, FileText, Settings, ChevronUp, ChevronDown, Pencil
} from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'textarea', label: 'Long Text', icon: FileText },
  { value: 'select', label: 'Dropdown', icon: List },
  { value: 'checkbox', label: 'Checkbox', icon: ToggleLeft },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'file', label: 'File Upload', icon: Upload },
  { value: 'location', label: 'Location', icon: MapPin },
];

const FORM_TYPES = [
  { value: 'service_request', label: 'Service Request' },
  { value: 'contact_form', label: 'Contact Form' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'onboarding', label: 'Employee Onboarding' },
  { value: 'custom', label: 'Custom' },
];

function FieldCard({ field, index, total, onUpdate, onDelete, onMove }) {
  const [expanded, setExpanded] = useState(false);
  const FieldIcon = FIELD_TYPES.find(t => t.value === field.type)?.icon || Type;

  return (
    <div className="border border-gray-border rounded-lg bg-white overflow-hidden" data-testid={`form-field-${index}`}>
      <div className="flex items-center gap-2 p-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <GripVertical className="h-4 w-4 text-gray-300 flex-shrink-0 cursor-grab" />
        <div className="w-7 h-7 rounded bg-blue-50 flex items-center justify-center flex-shrink-0">
          <FieldIcon className="h-3.5 w-3.5 text-blue-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-text-primary truncate">{field.label || 'Untitled Field'}</p>
          <p className="text-[10px] text-gray-400">{field.type} {field.required ? '· required' : ''}</p>
        </div>
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          {index > 0 && <button className="p-1 hover:bg-gray-100 rounded" onClick={() => onMove(index, index - 1)}><ChevronUp className="h-3 w-3 text-gray-400" /></button>}
          {index < total - 1 && <button className="p-1 hover:bg-gray-100 rounded" onClick={() => onMove(index, index + 1)}><ChevronDown className="h-3 w-3 text-gray-400" /></button>}
          <button className="p-1 hover:bg-red-50 rounded" onClick={() => onDelete(index)}><Trash2 className="h-3 w-3 text-red-400" /></button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-gray-100 p-3 space-y-3 bg-gray-50/50">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[10px]">Label</Label><Input value={field.label} onChange={e => onUpdate(index, { ...field, label: e.target.value })} className="mt-1 h-7 text-xs" /></div>
            <div><Label className="text-[10px]">Field Key</Label><Input value={field.key} onChange={e => onUpdate(index, { ...field, key: e.target.value })} className="mt-1 h-7 text-xs" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[10px]">Type</Label>
              <Select value={field.type} onValueChange={v => onUpdate(index, { ...field, type: v })}>
                <SelectTrigger className="mt-1 h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-[10px]">Placeholder</Label><Input value={field.placeholder || ''} onChange={e => onUpdate(index, { ...field, placeholder: e.target.value })} className="mt-1 h-7 text-xs" /></div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1.5 text-[10px]"><input type="checkbox" checked={field.required} onChange={e => onUpdate(index, { ...field, required: e.target.checked })} className="w-3 h-3 rounded" /> Required</label>
          </div>
          {field.type === 'select' && (
            <div><Label className="text-[10px]">Options (one per line)</Label>
              <Textarea value={(field.options || []).join('\n')} onChange={e => onUpdate(index, { ...field, options: e.target.value.split('\n').filter(Boolean) })} className="mt-1 text-xs min-h-[60px]" placeholder="Option 1&#10;Option 2&#10;Option 3" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FormPreview({ fields, formName }) {
  return (
    <Card className="bg-white border-gray-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2"><Eye className="h-4 w-4 text-purple-500" /> Preview: {formName || 'Untitled Form'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {fields.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-6">Add fields to see a preview</p>
        ) : fields.map((f, i) => (
          <div key={i}>
            <Label className="text-xs">{f.label} {f.required && <span className="text-red-500">*</span>}</Label>
            {f.type === 'textarea' ? <Textarea placeholder={f.placeholder} className="mt-1 text-sm" rows={2} disabled /> :
             f.type === 'select' ? <Select disabled><SelectTrigger className="mt-1"><SelectValue placeholder={f.placeholder || 'Select...'} /></SelectTrigger></Select> :
             f.type === 'checkbox' ? <div className="flex items-center gap-2 mt-1"><input type="checkbox" disabled className="w-4 h-4 rounded" /><span className="text-xs text-gray-400">{f.placeholder || f.label}</span></div> :
             <Input type={f.type === 'number' ? 'number' : f.type === 'email' ? 'email' : f.type === 'date' ? 'date' : 'text'} placeholder={f.placeholder} className="mt-1 text-sm" disabled />}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function FormBuilderPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [fields, setFields] = useState([]);
  const [formMeta, setFormMeta] = useState({ name: '', form_type: 'custom', description: '' });
  const [saving, setSaving] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/form-builder/templates`); setTemplates(data.templates || data || []); }
    catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const openTemplate = (t) => {
    setActiveTemplate(t);
    setFields(t.fields || []);
    setFormMeta({ name: t.name || '', form_type: t.form_type || 'custom', description: t.description || '' });
  };

  const addField = (type) => {
    const label = FIELD_TYPES.find(t => t.value === type)?.label || type;
    setFields(prev => [...prev, { key: `field_${prev.length + 1}`, label: `${label} Field`, type, required: false, placeholder: '', options: type === 'select' ? ['Option 1', 'Option 2'] : undefined }]);
  };

  const updateField = (idx, field) => setFields(prev => prev.map((f, i) => i === idx ? field : f));
  const deleteField = (idx) => setFields(prev => prev.filter((_, i) => i !== idx));
  const moveField = (from, to) => {
    setFields(prev => { const n = [...prev]; const [item] = n.splice(from, 1); n.splice(to, 0, item); return n; });
  };

  const saveForm = async () => {
    if (!formMeta.name) { toast.error('Form name required'); return; }
    setSaving(true);
    try {
      const payload = { ...formMeta, fields: fields.map((f, i) => ({ ...f, order: i })) };
      if (activeTemplate?.id) {
        await axios.put(`${API}/api/form-builder/templates/${activeTemplate.id}`, payload);
        toast.success('Form updated');
      } else {
        const { data } = await axios.post(`${API}/api/form-builder/templates`, payload);
        setActiveTemplate(data);
        toast.success('Form created');
      }
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const newForm = () => {
    setActiveTemplate({});
    setFields([]);
    setFormMeta({ name: '', form_type: 'custom', description: '' });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  // Template List View
  if (!activeTemplate) {
    return (
      <div className="space-y-6 animate-fadeIn" data-testid="form-builder-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Form Builder<PageInfoTooltip pageKey="form-builder" /></h1>
            <p className="text-gray-medium text-sm">Create and manage custom forms for your business</p>
          </div>
          <Button className="bg-emerald-brand hover:bg-emerald-600 gap-2" onClick={newForm} data-testid="new-form-btn"><Plus className="h-4 w-4" /> New Form</Button>
        </div>
        {templates.length === 0 ? (
          <Card className="bg-white border-gray-border"><CardContent className="p-8 text-center">
            <FileText className="h-12 w-12 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-medium">No forms created yet</p>
            <Button className="mt-3 bg-emerald-brand hover:bg-emerald-600" onClick={newForm}><Plus className="h-4 w-4 mr-2" /> Create Your First Form</Button>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {templates.map(t => (
              <Card key={t.id} className="bg-white border-gray-border hover:shadow-sm transition-shadow cursor-pointer" onClick={() => openTemplate(t)} data-testid={`form-template-${t.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-text-primary">{t.name}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">{t.form_type?.replace(/_/g, ' ')} · {(t.fields || []).length} fields</p>
                    </div>
                    <Badge className="bg-blue-100 text-blue-700 text-[10px]">{t.form_type}</Badge>
                  </div>
                  {t.description && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{t.description}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Form Editor View
  return (
    <div className="space-y-4 animate-fadeIn" data-testid="form-editor">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="link" className="text-xs text-gray-400 p-0 h-auto" onClick={() => setActiveTemplate(null)}>Back to Forms</Button>
          <h1 className="text-xl font-heading font-bold text-text-primary mt-1">{formMeta.name || 'New Form'}</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setActiveTemplate(null)}>Cancel</Button>
          <Button size="sm" className="bg-emerald-brand hover:bg-emerald-600 gap-1" onClick={saveForm} disabled={saving} data-testid="save-form-btn">
            {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Form Settings + Field Palette */}
        <div className="space-y-4">
          <Card className="bg-white border-gray-border">
            <CardHeader className="pb-2"><CardTitle className="text-xs flex items-center gap-1.5"><Settings className="h-3.5 w-3.5" /> Form Settings</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div><Label className="text-[10px]">Form Name *</Label><Input value={formMeta.name} onChange={e => setFormMeta(p => ({ ...p, name: e.target.value }))} className="mt-1 h-7 text-xs" data-testid="form-name-input" /></div>
              <div><Label className="text-[10px]">Type</Label>
                <Select value={formMeta.form_type} onValueChange={v => setFormMeta(p => ({ ...p, form_type: v }))}>
                  <SelectTrigger className="mt-1 h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{FORM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-[10px]">Description</Label><Textarea value={formMeta.description} onChange={e => setFormMeta(p => ({ ...p, description: e.target.value }))} className="mt-1 text-xs min-h-[40px]" /></div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-border">
            <CardHeader className="pb-2"><CardTitle className="text-xs flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Add Field</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-1.5">
                {FIELD_TYPES.map(ft => (
                  <button key={ft.value} onClick={() => addField(ft.value)}
                    className="flex items-center gap-2 p-2 rounded-lg border border-gray-100 hover:border-blue-300 hover:bg-blue-50 transition-all text-left"
                    data-testid={`add-field-${ft.value}`}>
                    <ft.icon className="h-3.5 w-3.5 text-blue-500" />
                    <span className="text-[10px] font-medium text-text-primary">{ft.label}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Center: Field List */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500">{fields.length} fields</p>
          {fields.length === 0 ? (
            <Card className="bg-white border-gray-border border-dashed"><CardContent className="p-6 text-center">
              <p className="text-xs text-gray-400">Click a field type to add it to your form</p>
            </CardContent></Card>
          ) : fields.map((f, i) => (
            <FieldCard key={i} field={f} index={i} total={fields.length} onUpdate={updateField} onDelete={deleteField} onMove={moveField} />
          ))}
        </div>

        {/* Right: Preview */}
        <div>
          <FormPreview fields={fields} formName={formMeta.name} />
        </div>
      </div>
    </div>
  );
}
